<template>
  <div class="mf-materials-inner">
    <div
      v-if="isLoading && !error"
      class="mf-materials-loading"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 200 200"
      >
        <circle
          fill="#80388d"
          stroke="#80388d"
          stroke-width="15"
          r="15"
          cx="40"
          cy="100"
        >
          <animate
            attributeName="opacity"
            calcMode="spline"
            dur="2"
            values="1;0;1;"
            keySplines=".5 0 .5 1;.5 0 .5 1"
            repeatCount="indefinite"
            begin="-.4"
          />
        </circle>
        <circle
          fill="#80388d"
          stroke="#80388d"
          stroke-width="15"
          r="15"
          cx="100"
          cy="100"
        >
          <animate
            attributeName="opacity"
            calcMode="spline"
            dur="2"
            values="1;0;1;"
            keySplines=".5 0 .5 1;.5 0 .5 1"
            repeatCount="indefinite"
            begin="-.2"
          />
        </circle>
        <circle
          fill="#80388d"
          stroke="#80388d"
          stroke-width="15"
          r="15"
          cx="160"
          cy="100"
        >
          <animate
            attributeName="opacity"
            calcMode="spline"
            dur="2"
            values="1;0;1;"
            keySplines=".5 0 .5 1;.5 0 .5 1"
            repeatCount="indefinite"
            begin="0"
          />
        </circle>
      </svg>
    </div>
    <div
      v-if="!isLoading && error"
      class="mf-materials-error"
    >
      <div class="mf-materials-error-heading">
        Es ist ein Fehler aufgetreten!
      </div>
      <div class="mf-materials-error-content">
        Bitte lade die Seite neu und versuche es später erneut!
      </div>
      <div
        v-if="error.message"
        class="mf-materials-error-message"
      >
        {{ error.message }}
      </div>
    </div>
    <div v-if="!isLoading && !error">
      <MaterialFilters
        :items="materials"
        @filter-changed="applyFilters"
        @filter-reset="resetEntries"
      />
      <MaterialEntries :items="paginatedMaterials" />
      <MaterialPagination
        v-model="currentPage"
        :total-items="totalItems"
        :items-per-page="itemsPerPage"
        @update:model-value="scrollToTop"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";

import MaterialEntries from "@components/MaterialEntries.vue";
import MaterialFilters from "@components/MaterialFilters.vue";
import MaterialPagination from "@components/MaterialPagination.vue";

import useStateStore from "@stores/state";
import useMaterialsStore from "@stores/materials";

// define stores
const stateStore = useStateStore();
const materialsStore = useMaterialsStore();

// get data + prepare categories
const isLoading = computed(() => stateStore.isLoading);
const error = computed(() => stateStore.error);
const materials = ref<IMaterialEntry[]>([]);

// define pagination settings
const totalItems = computed(() => materials.value.length);
const itemsPerPage = ref(20);
const currentPage = ref(1);

// compute paginated materials
const paginatedMaterials = computed(() => {
  const page = currentPage.value === 0 ? 1 : currentPage.value;
  const start = (page - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return materials.value.slice(start, end);
});

// onMounted
onMounted(async () => {
  try {
    // load materials
    materials.value = await materialsStore.getItems();

    // set loading to false
    stateStore.setLoading(false);
  } catch (error_) {
    console.error("Error parsing data prop", error_);
    stateStore.setLoading(false);
    stateStore.setError(new Error("Ein Fehler ist aufgetreten - bitte lade die Seite erneut!"));
  }
});

// transition page
const scrollToTop = () => {
  const materialsContainer = document.querySelector('mf-materials');
  if (materialsContainer) {
    materialsContainer.scrollIntoView({ behavior: 'smooth' });
  }
};

// apply filters
const applyFilters = (filters: IFilters) => {
  materials.value = materialsStore.filterItems(filters);
  currentPage.value = 1;
};

// reset entries
const resetEntries = () => {
  materials.value = materialsStore.items;
  currentPage.value = 1;
};
</script>
